import React, { Fragment, useState } from "react";

import Accordion from "@common/components/Accordion/Accordion";
import Button from "@common/components/Button/Button";
import { ActivityHistoryButton } from "@common/components/DetailsPane/ActivityHistoryButton";
import { DetailsPane } from "@common/components/DetailsPane/DetailsPane";
import { DetailsPaneNudge } from "@common/components/DetailsPane/DetailsPaneNudge";
import {
  getAppUserInfoElements,
  getTicketDetailsElements,
  GroupedTicketDetailsElement,
  readMetadata,
} from "@common/components/DetailsPane/helper";
import * as Icon from "@common/components/DetailsPane/icons";
import Heading from "@common/components/Heading/Heading";
import EditableTitle from "@common/components/Input/EditableTitle/EditableTitle";
import { ActivityHistoryNudge, MetadataNudge, UserDetailsNudge } from "@common/components/Nudge/Nudge";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useCrashesAttributes } from "@common/hooks/filtering/modules/useCrashesAttributes";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { PlatformName, PlatformOs } from "@common/models";
import { CrashFrame } from "@common/models/crashReporting";
import { docLink } from "@common/util/docs";
import identifiers from "@common/util/identifiers.json";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import AttachmentsContainer from "@main/pages/shared/components/Attachments/AttachmentsContainer";
import CustomFields from "@main/pages/shared/components/CustomFields/CustomFields";
import MessageContainer from "@main/pages/shared/components/Messages/MessageContainer";
import { TicketDetailsContainer } from "@main/pages/shared/components/TicketDetails/TicketDetailsContainer";
import { TicketContainer } from "@main/pages/shared/components/TicketDetails/TicketDetailsContainer.styles";
import { TicketInfoContainer } from "@main/pages/shared/components/TicketDetails/TicketInfoContainer";
import { TicketKeyCopy } from "@main/pages/shared/components/TicketKey/TicketKey";
import TicketMobileViewHeader from "@main/pages/shared/components/TicketMobileViewHeader/TicketMobileViewHeader";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import * as Styled from "./Crashes.styles";
import CrashesPageNavigation from "../components/PageNavigation/CrashesPageNavigation";
import CrashEventPageNavigationContent from "../components/PageNavigation/CrashEvent/CrashEventPageNavigationContent";
import useCrashEventApiConsumer from "../consumers/useCrashEventApiConsumer";
import useCrashEventMessagesConsumer from "../consumers/useCrashEventMessagesConsumer";
import useCrashOverviewApiConsumer from "../consumers/useCrashOverviewApiConsumer";

export default function CrashEventPageView() {
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const params = useParams<{ crashReportKey: string; crashReportEventId: string }>();
  const navigate = useNavigate();
  const [showAllThreads, setShowAllThreads] = useState(false);
  const [detailDropdownOpened, setDetailDropdownOpened] = useState<string | undefined>(undefined);

  const platform = Object.values(PlatformName).find((value) => value === selectedApp?.platform.name);

  const { addExternal } = useCrashesAttributes(true);

  const crashReportEventConsumer = useCrashEventApiConsumer({
    teamId: selectedWorkspace?.id,
    appId: selectedApp?.id,
    crashReportKey: params?.crashReportKey,
    crashReportEventId: params?.crashReportEventId,
  });

  const crashOverviewConsumer = useCrashOverviewApiConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    crashReportKey: params.crashReportKey,
  });

  const { messages, sendMessage, deleteMessage, editMessage } = useCrashEventMessagesConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    crashEventId: crashReportEventConsumer.crashEvent?.id,
  });

  const CustomElement = (
    <NavigationCustomElement>
      <div>
        <Tooltip
          text={"See all crashes"}
          position={"bottom"}
        >
          <Styled.NavbarNavigation
            onClick={() =>
              navigate(
                generatePath(RoutePaths.CRASH_REPORTS, {
                  workspaceSlug: selectedWorkspace?.slug ?? null,
                  appKey: selectedApp?.key ?? null,
                }),
              )
            }
          >
            <Icon.SendIcon
              height={20}
              width={20}
            />
          </Styled.NavbarNavigation>
        </Tooltip>
      </div>
      <CrashesPageNavigation crashTitle={crashOverviewConsumer.crashOverview?.title ?? ""}>
        <TicketKeyCopy
          ticketKey={crashOverviewConsumer.crashOverview?.key?.toString() ?? ""}
          enableCopy={true}
          marginLeft={8}
        />
        <CrashEventPageNavigationContent
          crashEvent={crashReportEventConsumer.crashEvent}
          crashReportKey={params.crashReportKey}
          selectedAppKey={selectedApp?.key}
          selectedWorkspaceSlug={selectedWorkspace?.slug}
          navigate={navigate}
        />
      </CrashesPageNavigation>
    </NavigationCustomElement>
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (!crashReportEventConsumer.crashEvent || !params.crashReportKey) {
    return <Spinner />;
  }

  const addToFilterObject = {
    addToFilter: addExternal,
    redirectURL: generatePath(RoutePaths.CRASH_REPORTS, {
      workspaceSlug: selectedWorkspace?.slug ?? null,
      appKey: selectedApp?.key ?? null,
    }),
  };

  const getMetadataElements = (metadata: { [key: string]: string } | undefined): Array<GroupedTicketDetailsElement> => {
    if (!metadata || Object.keys(metadata).length == 0) return [];

    return [
      {
        infoElements: readMetadata(metadata, [], detailDropdownOpened, setDetailDropdownOpened),
      },
    ];
  };

  const infoElements = [
    {
      title: "Crash event details",
      headingElement: crashReportEventConsumer?.crashEvent?.log ? (
        <ActivityHistoryButton
          to={generatePath(RoutePaths.CRASH_REPORT_LOGS, {
            workspaceSlug: selectedWorkspace?.slug ?? null,
            appKey: selectedApp?.key ?? null,
            crashReportKey: params.crashReportKey ?? null,
            crashReportEventId: crashReportEventConsumer.crashEvent?.id ?? null,
          })}
        />
      ) : (
        <ActivityHistoryNudge />
      ),
      infoElements: getTicketDetailsElements(
        crashReportEventConsumer.crashEvent,
        detailDropdownOpened,
        setDetailDropdownOpened,
        selectedApp,
        addToFilterObject,
        crashReportEventConsumer.blackbox,
      ),
    },
    {
      title: "Ticket metadata",
      infoElements: getMetadataElements(crashReportEventConsumer?.crashEvent?.metadata_),
      link: {
        tooltipText: "Set ticket metadata",
        link: docLink(platform).addTicketMetadata,
        isExternal: true,
        testId: identifiers["details.pane.metadata.link.add"],
      },
      emptyView: (
        <DetailsPaneNudge>
          <MetadataNudge />
        </DetailsPaneNudge>
      ),
    },
    {
      title: "User details",
      infoElements: getAppUserInfoElements(crashReportEventConsumer.crashEvent?.app_user, () => {
        window.open(
          generatePath(RoutePaths.USER_DETAILS, {
            workspaceSlug: selectedWorkspace?.slug ?? null,
            appKey: selectedApp?.key ?? null,
            userId: crashReportEventConsumer.crashEvent?.app_user?.id ?? null,
          }),
          "_blank",
        );
      }),
      link: {
        tooltipText: "Register app user",
        link: docLink(platform).registerAppUser,
        isExternal: true,
      },
      emptyView: (
        <DetailsPaneNudge>
          <UserDetailsNudge />
        </DetailsPaneNudge>
      ),
    },
  ];

  return (
    <PageWrap>
      <TicketContainer>
        <TicketInfoContainer>
          <TicketMobileViewHeader app={selectedApp} />
          <Heading
            as="h2"
            heading2
            marginBottom={40}
          >
            <EditableTitle
              value={crashReportEventConsumer.crashEvent?.pretty_title}
              defaultValue="No description"
              onEditConfirm={(title?: string) => crashReportEventConsumer.updateCrashEvent({ title })}
              testId={identifiers["central.column.crash.title"]}
            />
          </Heading>
          <CustomFields customFields={crashReportEventConsumer.crashEvent.custom_fields} />
          <Styled.Threads>
            {crashReportEventConsumer.crashEvent.threads.map((thread) => {
              if (!showAllThreads && !thread.is_blamed) return;
              return thread.exceptions.map((exception) => {
                const title = exception.exception_type ? exception.exception_type : thread.thread_name;
                return (
                  <Accordion
                    testId={identifiers["central.column.crash.thread.heading"]}
                    key={exception.id}
                    title={title}
                    content={getContent(exception.frames, selectedApp?.platform.os)}
                    subtitle={exception.exception_message}
                    isOpen={!showAllThreads ? true : undefined}
                  />
                );
              });
            })}
          </Styled.Threads>
          <Styled.Flex>
            <Button
              styling="textual"
              onClick={() => setShowAllThreads(!showAllThreads)}
              testId={identifiers["central.column.crash.link.allThreads"]}
            >
              {showAllThreads ? "Hide all threads" : "Show all threads"}
            </Button>

            {/*  <Button
              onClick={() =>
                displayToast({

                  content: "TODO - Implement download functionality",
                })
              }
              styling="textual"
            >
              Download
            </Button> */}
            {/*  <Button
              onClick={() =>
                displayToast({

                  content: "TODO - Implement copy stack trace functionality",
                })
              }
              styling="textual"
            >
              Copy stack trace
            </Button> */}
          </Styled.Flex>
          <AttachmentsContainer
            deviceOrientation={crashReportEventConsumer.crashEvent.pretty_device_orientation}
            attachments={crashReportEventConsumer.attachments.sort((a, b) => a.num - b.num)}
          />
          <MessageContainer
            messages={messages}
            onMessageSend={sendMessage}
            onMessageDelete={deleteMessage}
            onMessageEdit={editMessage}
            appUser={crashReportEventConsumer.crashEvent.app_user}
          />
        </TicketInfoContainer>
        <TicketDetailsContainer>
          <DetailsPane
            info={infoElements}
            headerTestId={identifiers["crash.reports.details.pane.header"]}
          />
        </TicketDetailsContainer>
      </TicketContainer>
    </PageWrap>
  );
}

const getContent = (frames: CrashFrame[], os?: PlatformOs) => {
  if (!os) return <Fragment />;
  return (
    <Fragment>
      {frames.map((frame) => {
        return (
          <Styled.Row
            key={frame.id}
            $isBlamedApp={frame.is_blamed && frame.is_application}
            $isApp={frame.is_application && !frame.is_blamed}
          >
            <Styled.Column>{frame.index}</Styled.Column>
            <Styled.Column>{getSecondColumn(frame, os)}</Styled.Column>
            <Styled.Column>{getThirdColumn(frame, os)}</Styled.Column>
          </Styled.Row>
        );
      })}
    </Fragment>
  );
};

const getSecondColumn = (frame: CrashFrame, os: PlatformOs) => {
  if (os === PlatformOs.ANDROID) {
    return `${frame.symbol_name ? frame.symbol_name : frame.method}`;
  }

  return `${frame.object_name}`;
};

const getThirdColumn = (frame: CrashFrame, os: PlatformOs) => {
  if (os === PlatformOs.ANDROID) {
    return `${frame.file}: ${frame.line}`;
  }

  if (frame.method) {
    return `${frame.file ? frame.file : ""}
    ${frame.file && frame.line ? " - Line " + frame.line + " " : ""}
    ${frame.method + " + " + frame.line}`;
  }

  return `${frame.instruction_addr}
        ${frame.object_addr + " + " + (frame.instruction_addr - frame.object_addr)}
        ${frame.symbol_name ? frame.symbol_name + " + " + frame.offset : ""}`;
};
